import React, { useEffect, useRef, useState } from 'react';
import {
  FlyoutContainer,
  ToastNotification,
  ToastNotificationVariant,
  ComboboxOption
} from '@cmhc-schl/hearth-ui';
import { Box } from '@mui/material';
import { useAuthenticationContext } from 'Foundation/Authentication';
import { useTranslation } from 'react-i18next';
import i18n from 'i18next';
import styles from './style.module.scss';
import { useQueryClient } from '@tanstack/react-query';
import ErrorLoansModal from '../ErrorLoansModal';
import {
  ForceStatusFlyoutProps,
  Loan,
  LoanStatusResponse,
  PoolProcessingStatusCode,
  RationaleOption,
  RemarksDropDownHeader,
  ValidationRemarkPayload
} from './types';
import { isRemarkOther } from 'Feature/V2/Shared_Portfolio/utils/remarkOther';
import { overrideLoanStatus, validateOverrideLoanStatus } from './service/forceStatusService';
import ErrorStatusModal from '../ErrorStatusModal';
import ErrorMessage, {
  SpinnerElement
} from '../../../HbtMasterPoolLoansInventoryTable/components/ErrorMessage';
import {
  getDecisionOverrideRationales,
  patchDecisionOverride
} from 'Feature/V2/HbtPortfolioManagement/components/HbtPortfolioManagementRequestOverview/components/HbtIndividualLoanDetails/individualLoanDetailsService';
import { DecisionOverrideRationales } from 'Feature/V2/HbtPortfolioManagement/components/HbtPortfolioManagementRequestOverview/components/HbtIndividualLoanDetails/types';
import {
  comboboxRationale,
  FlyoutStatus,
  FlyoutType,
  generateValidationPayload
} from './Utils/utils';
import { useLocation } from 'react-router-dom';
import { LoanInventoryTableFields } from 'Feature/V2/HbtPortfolioManagement/components/HbtPortfolioManagementRequestOverview/components/HbtPortfolioLoansInventoryTable/types';
import { SuccessResult } from './components/SuccessResult';
import { MainComponent } from './components/MainComponent';
import {
  getMasterPoolAccountStatusCode,
  getUrlParam
} from '../../../HbtMasterPoolDetails/utils/masterPoolDetailsUtils';
import { MasterPoolStatusEnum } from '../../../HbtMasterPoolTable/types';
import { useDecisionOverrideRationales } from 'Feature/V2/HbtPortfolioManagement/components/HbtPortfolioManagementRequestOverview/components/HbtIndividualLoanDetails/hooks/useDecisionOverrideRationales';

interface Remark {
  remarkID: number | undefined;
  remarkMessage: string;
}
const largeFormFieldMaxChar = 300;

const ForceStatusFlyout: React.FC<ForceStatusFlyoutProps> = ({
  openFlyout,
  toggleDrawer,
  loans,
  loansData,
  masterPoolCmhcAccountNumber,
  isFromLoansInventory,
  radioButtonOptions,
  requestUrl,
  typeOfFlyout,
  headerText,
  loansToBeUpdatedTitle,
  loansTypeToBeAdded,
  textFieldPlaceholder,
  textFieldButtonText,
  addLoansDescription,
  addLoansTextFieldError,
  toasterHeader,
  toasterDesc,
  addLoansTextFieldErrorTwo,
  remarksTextField,
  remarksDropDownHeader,
  flyoutTitle,
  successFlyoutDesc,
  successFlyoutTitle,
  copyButtonText,
  copiedButtonText,
  overrideModalDesc,
  overrideModalTitle,
  errorModalDesc,
  errorModalHeader,
  errorModalProceedText,
  loansUpdateLargeFormFieldButtonText,
  callToaster,
  typeOfPortfolioInsurance = 'masterPool',
  setMasterPoolNotification,
  setNotificationBannerConfig,
  setIsForceStatusTriggered
}) => {
  const { t } = useTranslation('HbtMasterPool');
  const [toaster, setToasterMsg] = useState({
    icon: 'icon_check_circle',
    message: {
      header: t('PI-Portfolio-Overview-Flyout-Toaster-Header-Success'),
      subHeader: String(' ')
    }
  });
  const queryClient = useQueryClient();
  const [showSuccessResult, setShowSuccessResult] = useState(false);
  const [successfulLoans, setSuccessfulLoans] = useState<LoanStatusResponse[]>([]);
  const [showToaster, setShowToaster] = useState(false);
  const [errorModalOpen, setErrorModalOpen] = useState(false);
  const [rationaleOptions, setRationaleOptions] = useState<RationaleOption[]>([]);
  const [proceedWithEligibleLoansSignal, setProceedWithEligibleLoansSignal] = useState(0);
  const [newLoansInput, setNewLoansInput] = useState(loans ?? '');
  const [errorLoans, setErrorLoans] = useState<Loan[]>([]);
  const [eligibleLoans, setEligibleLoans] = useState<Loan[]>([]);
  const [isAddingLoans, setIsAddingLoans] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [approvalStatus, setApprovalStatus] = useState<string | null>(null);
  const [statusRequiredError, setStatusRequiredError] = useState(false);
  const [rationaleRequired, setRationaleRequired] = useState(false);
  const [largeFormFieldRequired, setLargeFormFieldRequired] = useState(false);
  const [showStatusChangeModal, setShowStatusChangeModal] = useState(false);
  const [flyoutLoans, setFlyoutLoans] = useState<Loan[]>([]);
  const [showErrorResult, setShowErrorResult] = useState(false);
  const [largeFormFieldValue, setLargeFormFieldValue] = useState('');
  const [largeFormFieldErrMsg, setLargeFormFieldErrMsg] = useState(
    t('Add-Remarks-Required-Error-Msg')
  );
  const [addLoanslargeFormFieldErrMsg, setAddLoansLargeFormFieldErrMsg] = useState('');
  const [showAddSection, setShowAddSection] = useState(flyoutLoans.length === 0);
  const [remark, setRemark] = useState<Remark>({
    remarkID: undefined,
    remarkMessage: ''
  });
  const authenticationContext = useAuthenticationContext();
  let pptCode: string | null = null;
  let applicationID: string | null = null;
  if (typeOfFlyout && ['updateLoan', 'updateRemark'].includes(typeOfFlyout)) {
    const { search } = useLocation();
    const searchParams = new URLSearchParams(search);
    pptCode = searchParams.get('pptCode');
    applicationID = searchParams.get('appID');
  }

  useEffect(() => {
    if (openFlyout && loans != null) {
      setNewLoansInput(loans);
    }
  }, [openFlyout, loans]);

  const hasFetchedRationales = useRef(false);
  const { data: rationale } = useDecisionOverrideRationales(authenticationContext);

  const onErrorLoans = (loans: Loan[], eligibleLoans?: Loan[]) => {
    setErrorLoans(loans);
    setErrorModalOpen(true);
  };

  const handleProceedStatusChange = () => {
    setApprovalStatus(approvalStatus);
    setNewLoansInput('');
    setFlyoutLoans([]);
    setShowStatusChangeModal(false);
  };

  const handleCancelStatusChange = () => {
    const previousOption =
      radioButtonOptions?.find((option) => option.value !== approvalStatus) ?? null;
    setApprovalStatus(previousOption?.value ?? null);
    setShowStatusChangeModal(false);
  };

  const onProceedWithEligibleLoans = (elibleLoans: Loan[]) => {
    if (eligibleLoans.length > 0) {
      setFlyoutLoans([...elibleLoans]);
      handleToastNotification(
        'icon_check_circle',
        toasterHeader ?? t('Loan-Add-Success-Toast'),
        toasterDesc ?? t('Loan-Add-Success-Toast-Desc')
      );
      setNewLoansInput('');
      setShowAddSection(false);
      setProceedWithEligibleLoansSignal((s) => s + 1);
    }
    closeErrorModal();
  };

  const handleRemarkChange = (option: ComboboxOption) => {
    if (!option) {
      setRemark({
        remarkID: undefined,
        remarkMessage: ''
      });
      return;
    }
    const remarkID = Number(option.value);
    const remarkMessage = option.label;

    const isOther = isRemarkOther({ remarkID, remarkMessage });

    setRemark({
      remarkID,
      remarkMessage: isOther ? '' : remarkMessage
    });

    setRationaleRequired(false);

    if (isOther) {
      setLargeFormFieldValue('');
    } else {
      setLargeFormFieldRequired(false);
      setLargeFormFieldErrMsg('');
    }
  };

  const generatePayloadToOveride = (
    loanNumbers: string[],
    status: string | null,
    remark: Remark,
    masterPoolCmhcAccountNumber?: string,
    typeOfFlyout?: FlyoutType,
    pptCode?: string | null,
    loans?: LoanInventoryTableFields[],
    applicationId?: string | null
  ) => {
    if (typeOfPortfolioInsurance === 'masterPool' && typeOfFlyout === 'updateRemark') {
      return {
        loans: loanNumbers,
        validateOnly: false,
        masterPoolCmhcAccountNumber,
        remark: {
          remarkId: Number(remark.remarkID),
          remarkText: isRemarkOther(remark) ? largeFormFieldValue : remark.remarkMessage
        }
      };
    }

    if (typeOfPortfolioInsurance === 'validationReview' && loans && applicationID) {
      if (typeOfFlyout === 'updateLoan' || typeOfFlyout === 'updateRemark') {
        const payload = {
          poolprecitiontypecode: pptCode ?? undefined,
          validateOnly: false,
          targetStatus: status ?? undefined,
          portfolioIds: loans.map((loan) => Number(loan.portfolioId)),
          applicationId: Number(applicationId) ?? undefined,
          decisionOverrideRationaleId: remark.remarkID,
          decisionOverrideRemarks: isRemarkOther(remark)
            ? largeFormFieldValue
            : remark.remarkMessage
        };

        return pptCode === PoolProcessingStatusCode.CANCELLED
          ? { ...payload, cmhcAccountNumbers: loans.map((loan) => String(loan.cmhcAccountNumber)) }
          : {
              ...payload,
              lenderReferenceNumbers: loans.map((loan) => String(loan.lenderReferenceNumber))
            };
      }
    }

    return {
      loans: loanNumbers,
      validateOnly: false,
      targetStatus: status ?? '',
      remark: {
        remarkId: Number(remark.remarkID),
        remarkText: isRemarkOther(remark) ? largeFormFieldValue : remark.remarkMessage
      }
    };
  };

  const handleLargeFormField = (value: string) => {
    const isTooLong = value.length > largeFormFieldMaxChar;
    const isEmpty = !value.trim();
    setLargeFormFieldValue(value);

    if (isTooLong || isEmpty) {
      setLargeFormFieldErrMsg(
        isTooLong
          ? t('PI-Portfolio-Overview-Flyout-Large-Form-Field-Char-Limit')
          : t('Add-Remarks-Required-Error-Msg')
      );
      setLargeFormFieldRequired(true);
    } else {
      setLargeFormFieldRequired(false);
    }
  };

  const submitOverrideLoanStatus = async () => {
    let hasError = false;

    if (!approvalStatus) {
      setStatusRequiredError(true);
    }

    if (flyoutLoans.length === 0) {
      setAddLoansLargeFormFieldErrMsg(
        pptCode === PoolProcessingStatusCode.NON_CANCELLED
          ? t('Add-Loans-Empty-Error-Msg-Non-Cancelled')
          : t('Add-Loans-Empty-Error-Msg-Cancelled')
      );
      hasError = true;
    }

    if (!remark.remarkID) {
      setRationaleRequired(true);
      hasError = true;
    } else {
      setRationaleRequired(false);
    }

    if (isRemarkOther(remark)) {
      const isEmpty = !largeFormFieldValue.trim();
      if (isEmpty) {
        setLargeFormFieldErrMsg(remarksTextField ?? t('Add-Remarks-Required-Error-Msg'));
        setLargeFormFieldRequired(true);
        hasError = true;
      } else if (largeFormFieldValue.length > largeFormFieldMaxChar) {
        hasError = true;
      } else {
        setLargeFormFieldRequired(false);
      }
    }

    if (hasError) return;

    try {
      setIsSubmitting(true);

      let response: any;
      if (typeOfPortfolioInsurance === 'validationReview') {
        const flyoutLoansData = flyoutLoans.map((loan) => loan.validationReviewLoanData!);
        const portfolioId = getUrlParam('pfID');
        let listOfPortfolioId: number[] = [];
        if (Boolean(portfolioId)) {
          listOfPortfolioId = portfolioId.split(',').map(Number);
        }

        let payload = generateValidationPayload(
          flyoutLoansData,
          approvalStatus,
          false,
          pptCode,
          applicationID,
          listOfPortfolioId,
          loansData,
          remark,
          largeFormFieldValue
        );
        if (typeOfFlyout === 'updateRemark') {
          response = await patchDecisionOverride({
            authenticationContext,
            request: payload as ValidationRemarkPayload
          });
        } else {
          response = await validateOverrideLoanStatus({
            authenticationContext,
            payload: payload as ValidationRemarkPayload,
            requestQuery: requestUrl
          });
        }
      } else {
        const loanNumbers = flyoutLoans.map((loan) => loan.accountNumber);
        let payload = generatePayloadToOveride(
          loanNumbers,
          approvalStatus,
          remark,
          masterPoolCmhcAccountNumber,
          typeOfFlyout,
          pptCode,
          loansData,
          applicationID
        );
        response = await overrideLoanStatus({
          authenticationContext,
          payload,
          requestQuery: requestUrl
        });
        const isRisked = getMasterPoolAccountStatusCode() === MasterPoolStatusEnum.Risked;

        typeOfFlyout !== 'updateRemark' &&
          isRisked &&
          setTimeout(() => {
            setIsForceStatusTriggered && setIsForceStatusTriggered(true);
            setMasterPoolNotification && setMasterPoolNotification(true);
            setNotificationBannerConfig &&
              setNotificationBannerConfig({
                header: t('Master-Pool-Re-Run-Pricing-Banner-Header'),
                subHeader: t('Master-Pool-Re-Run-Pricing-Banner-Content')
              });
          }, 4500);
      }

      if (typeOfPortfolioInsurance === 'validationReview') {
        queryClient.invalidateQueries({ queryKey: ['portfolio-inventory-loans'] });
        queryClient.invalidateQueries({ queryKey: ['loan'] });
        handleToastNotification(
          'icon_check_circle',
          typeOfFlyout === 'updateRemark'
            ? t('Remark-Adding-Sucess')
            : t('Status-Change-Success-Toast'),
          typeOfFlyout === 'updateRemark'
            ? t('Remark-Adding-Sucess-Body')
            : t('Status-Change-Success-Toast-Body')
        );
        closeFlyout();
        return;
      }
      queryClient.invalidateQueries({
        queryKey: ['masterpool-inventory-loans']
      });
      queryClient.invalidateQueries({ queryKey: ['loan'] });
      handleToastNotification(
        'icon_check_circle',
        typeOfFlyout === 'updateRemark'
          ? t('Remark-Adding-Sucess')
          : t('Status-Change-Success-Toast'),
        typeOfFlyout === 'updateRemark'
          ? t('Remark-Adding-Sucess-Body')
          : t('Status-Change-Success-Toast-Body')
      );
      closeFlyout();
    } catch (error) {
      setShowErrorResult(true);
      handleToastNotification(
        'icon_error_outlined',
        typeOfFlyout === 'updateRemark'
          ? t('Remark-Adding-Unsucessfull')
          : t('Status-Change-Unsuccessul-Toast'),
        typeOfFlyout === 'updateRemark'
          ? t('Remark-Adding-Unsucessfull-Body')
          : t('Status-Change-Unsuccessul-Toast-Body')
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const closeErrorModal = () => setErrorModalOpen(false);

  const handleToastNotification = (icon: string, message: string, desc = '') => {
    if (callToaster) {
      return callToaster(icon, message, desc);
    }
    setToasterMsg({
      icon,
      message: {
        header: message,
        subHeader: desc
      }
    });

    setShowToaster(true);
  };

  const closeFlyout = () => {
    setProceedWithEligibleLoansSignal(0);
    setErrorModalOpen(false);
    setErrorLoans([]);
    setFlyoutLoans([]);
    toggleDrawer(false);
    setApprovalStatus(null);
    setStatusRequiredError(false);
    setShowStatusChangeModal(false);
    setShowToaster(false);
    setShowSuccessResult(false);
    setShowErrorResult(false);
    setSuccessfulLoans([]);
    setRemark({ remarkID: undefined, remarkMessage: '' });
    setRationaleRequired(false);
    setLargeFormFieldRequired(false);
    setAddLoansLargeFormFieldErrMsg('');
    setNewLoansInput('');
  };

  const getGroupName = (
    rationale: DecisionOverrideRationales,
    typeOfFlyout?: FlyoutType,
    remarksDropDownHeader?: RemarksDropDownHeader[]
  ) => {
    if (!typeOfFlyout || typeOfFlyout === 'updateLoan') return '';

    if (remarksDropDownHeader) {
      const groupName = remarksDropDownHeader.find((remark) => remark.ids.includes(rationale.id));
      return groupName ? groupName.groupName : '';
    }

    const remarksIDsForPassedStatus = [1, 2, 3, 4, 999];
    if (typeOfFlyout === 'updateRemark') {
      return remarksIDsForPassedStatus.includes(rationale.id)
        ? t('Approved-Group-Rationale')
        : t('Withdrawn-Group-Rationale');
    }

    return '';
  };

  useEffect(() => {
    const decisionRationales = comboboxRationale(
      typeOfFlyout ?? 'changeStatus',
      rationale ?? [],
      approvalStatus as FlyoutStatus,
      pptCode,
      typeOfPortfolioInsurance,
      remarksDropDownHeader
    );

    const options = decisionRationales.map((rationale) => ({
      label: i18n.language === 'en' ? rationale.englishRationale : rationale.frenchRationale,
      value: rationale.id.toString().padStart(4, '0'),
      isGroup: typeOfFlyout === 'updateRemark',
      groupName: getGroupName(rationale, typeOfFlyout, remarksDropDownHeader)
    }));

    setRationaleOptions(options);
  }, [
    approvalStatus,
    rationale,
    pptCode,
    remarksDropDownHeader,
    typeOfFlyout,
    typeOfPortfolioInsurance
  ]);

  return (
    <FlyoutContainer
      id={styles.ForceStatusFlyout}
      isHeaderRequired={true}
      headerText={flyoutTitle ?? t('Override-Loan-Status')}
      open={openFlyout}
      onClose={closeFlyout}
      content={
        <>
          {
            <Box
              className={`${styles.flyoutContainer} ${styles.flyoutMainContainer}`}
              role="presentation"
            >
              <Box className={styles.flyoutContent}>
                {showSuccessResult ? (
                  <SuccessResult
                    pptCode={pptCode}
                    typeOfFlyout={typeOfFlyout}
                    successFlyoutDesc={successFlyoutDesc}
                    successFlyoutTitle={successFlyoutTitle}
                    successfulLoans={successfulLoans}
                    loansTypeToBeAdded={loansTypeToBeAdded}
                    typeOfPortfolioInsurance={typeOfPortfolioInsurance}
                    flyoutLoans={flyoutLoans}
                    copiedButtonText={copiedButtonText}
                    copyButtonText={copyButtonText}
                    remark={remark}
                  />
                ) : showErrorResult ? (
                  <Box className={styles.errorContainer}>
                    <ErrorMessage errorType="api_error" />
                  </Box>
                ) : (
                  <MainComponent
                    largeFormFieldValue={largeFormFieldValue}
                    setApprovalStatus={setApprovalStatus}
                    headerText={headerText}
                    setEligibleLoans={setEligibleLoans}
                    setShowAddSection={setShowAddSection}
                    showAddSection={showAddSection}
                    setShowStatusChangeModal={setShowStatusChangeModal}
                    radioButtonOptions={radioButtonOptions}
                    loansToBeUpdatedTitle={loansToBeUpdatedTitle}
                    flyoutLoans={flyoutLoans}
                    onNotify={handleToastNotification}
                    onErrorLoans={onErrorLoans}
                    proceedWithEligibleLoans={proceedWithEligibleLoansSignal}
                    setFlyoutLoans={setFlyoutLoans}
                    approvalStatus={approvalStatus}
                    statusRequiredError={statusRequiredError}
                    setStatusRequiredError={setStatusRequiredError}
                    newLoansInput={newLoansInput}
                    setNewLoansInput={setNewLoansInput}
                    addLoanslargeFormFieldErrMsg={addLoanslargeFormFieldErrMsg}
                    setAddLoansLargeFormFieldErrMsg={setAddLoansLargeFormFieldErrMsg}
                    loansTypeToBeAdded={loansTypeToBeAdded}
                    textFieldPlaceholder={textFieldPlaceholder}
                    textFieldButtonText={textFieldButtonText}
                    addLoansDescription={addLoansDescription}
                    addLoansTextFieldError={addLoansTextFieldError}
                    toasterHeader={toasterHeader}
                    toasterDesc={toasterDesc}
                    addLoansTextFieldErrorTwo={addLoansTextFieldErrorTwo}
                    typeOfFlyout={typeOfFlyout}
                    typeOfPortfolioInsurance={typeOfPortfolioInsurance}
                    pptCode={pptCode}
                    applicationID={applicationID}
                    loansData={loansData}
                    isFromLoansInventory={isFromLoansInventory}
                    masterPoolCmhcAccountNumber={masterPoolCmhcAccountNumber}
                    requestUrl={requestUrl}
                    proceedWithEligibleLoansSignal={proceedWithEligibleLoansSignal}
                    handleRemarkChange={handleRemarkChange}
                    rationaleRequired={rationaleRequired}
                    setRationaleRequired={setRationaleRequired}
                    rationaleOptions={rationaleOptions}
                    remark={remark}
                    largeFormFieldRequired={largeFormFieldRequired}
                    largeFormFieldErrMsg={largeFormFieldErrMsg}
                    isSubmitting={isSubmitting}
                    handleLargeFormField={handleLargeFormField}
                    submitOverrideLoanStatus={submitOverrideLoanStatus}
                    closeFlyout={closeFlyout}
                    setIsAddingLoans={setIsAddingLoans}
                    isAddingLoans={isAddingLoans}
                    loansUpdateLargeFormFieldButtonText={loansUpdateLargeFormFieldButtonText}
                  />
                )}
              </Box>
              <Box className={styles.toasterNotification}>
                <ToastNotification
                  dismissMode="autoAndActionable"
                  variant={ToastNotificationVariant.INFO}
                  open={showToaster}
                  header={{ value: toaster.message.header }}
                  subHeader={{ value: toaster.message.subHeader }}
                  onClose={() => setShowToaster(false)}
                  icon={toaster.icon}
                />
              </Box>
              <ErrorLoansModal
                open={errorModalOpen}
                loans={errorLoans}
                eligibleLoans={eligibleLoans}
                onClose={closeErrorModal}
                onProceed={onProceedWithEligibleLoans}
                errorModalIntroText={errorModalDesc}
                loansTypeToBeAdded={loansTypeToBeAdded}
                isAddRemarks={typeOfFlyout === 'updateRemark'}
                pptCode={pptCode ?? undefined}
                typeOfFlyout={typeOfFlyout}
                copyButtonText={copyButtonText}
                copiedButtonText={copiedButtonText}
                typeOfPortfolioInsurance={typeOfPortfolioInsurance}
                errorModalHeader={errorModalHeader}
                errorModalProceedText={errorModalProceedText}
              />
              <ErrorStatusModal
                open={showStatusChangeModal}
                onClose={handleCancelStatusChange}
                onProceed={handleProceedStatusChange}
                title={overrideModalTitle ?? t('Override-Status-Change-Modal-Title')}
                introText={overrideModalDesc ?? t('Override-Status-Change-Modal-Body')}
                proceedText={t('Override-Status-Change-Modal-Proceed')}
                cancelText={t('Cancel')}
              />
            </Box>
          }
        </>
      }
    />
  );
};

export default ForceStatusFlyout;
